import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, ModalBody } from "reactstrap";
import { Mail, Phone, Globe, Copy, Check, X, UserCheck, AlertTriangle } from "react-feather";
import ProfileAvatar from "../../../components/ProfileAvatar/ProfileAvatar";
import { getDisplayInitials } from "../../../utils/profileImage";
import { getImpersonatorSession, startImpersonation } from "../../../utils/impersonation";
import { impersonateUser } from "../../../actions/superAdminActions";

const display = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (value === "-") return "—";
  return value;
};

const isLink = (value) => {
  if (!value || value === "-" || value === "—") return false;
  return /^(https?:\/\/|www\.)/i.test(String(value));
};

const toHref = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "#";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const InfoItem = ({ label, value, isUrl = false }) => {
  const shown = display(value);
  const linkable = isUrl && shown !== "—" && isLink(shown);

  return (
    <div className="ud-modal__item">
      <span className="ud-modal__item-label">{label}</span>
      {linkable ? (
        <a
          className="ud-modal__item-value ud-modal__item-value--link"
          href={toHref(shown)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {shown}
        </a>
      ) : (
        <span className="ud-modal__item-value">{shown}</span>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="ud-modal__section">
    <h4 className="ud-modal__section-title">{title}</h4>
    <div className="ud-modal__grid">{children}</div>
  </section>
);

function UserDetailsModal({ isOpen, user, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState(null);

  const fullName = useMemo(() => {
    if (!user) return "";
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }, [user]);

  const initials = useMemo(
    () => getDisplayInitials(fullName, user?.email),
    [fullName, user]
  );

  const hasProfileImage = Boolean(
    user?.profile_image && user.profile_image !== "-"
  );

  // Reset all transient states when the modal opens with a different user or closes
  useEffect(() => {
    setImgError(false);
    setCopied(false);
    setConfirmPending(false);
    setImpersonating(false);
    setImpersonateError(null);
  }, [user?.id, user?.profile_image, isOpen]);

  if (!user) return null;

  const copyEmail = async () => {
    if (!user.email) return;
    try {
      await navigator.clipboard.writeText(user.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      // ignore clipboard failures
    }
  };

  const cardLabel =
    user.card_brand && user.card_brand !== "-"
      ? `${user.card_brand} •••• ${user.card_last_four || ""}`
      : "—";

  const showImage = hasProfileImage && !imgError;

  // Whether the Impersonate button should be shown at all:
  // hidden only when already inside an impersonation session (prevents nesting).
  // Account status is NOT checked here — the super-admin must be able to
  // impersonate any regular user including free/disabled accounts.
  // The backend still blocks impersonation of other super-admins.
  const canImpersonate = !getImpersonatorSession();

  const handleImpersonateConfirm = async () => {
    setImpersonating(true);
    setImpersonateError(null);
    try {
      const tokenData = await dispatch(impersonateUser(user.id));
      startImpersonation(tokenData, user, dispatch);
      onClose();
      navigate("/admin/dashboard");
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Impersonation failed. Please try again.";
      setImpersonateError(msg);
      setImpersonating(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="xl"
      className="ud-modal-dialog"
      contentClassName="ud-modal-content"
    >
      <ModalBody className="ud-modal-body">
        <div className="ud-modal__hero">
          <button
            type="button"
            className="ud-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} />
          </button>

          <div className="ud-modal__hero-main">
            <div className="ud-modal__avatar-wrap">
              {showImage ? (
                <div className="ud-modal__avatar">
                  <ProfileAvatar
                    key={`${user.id}-${user.profile_image}`}
                    src={user.profile_image}
                    alt={fullName || "User profile"}
                    onError={() => setImgError(true)}
                  />
                </div>
              ) : (
                <div className="ud-modal__avatar ud-modal__avatar--fallback">
                  {initials || "U"}
                </div>
              )}
            </div>

            <div className="ud-modal__hero-text">
              <h2 className="ud-modal__name">{fullName || "Unnamed user"}</h2>
              <div className="ud-modal__email-row">
                <Mail size={14} />
                <span>{display(user.email)}</span>
                {user.email ? (
                  <button
                    type="button"
                    className="ud-modal__copy"
                    onClick={copyEmail}
                    title="Copy email"
                  >
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                ) : null}
              </div>

              <div className="ud-modal__quick-meta">
                {user.phone && user.phone !== "-" ? (
                  <span>
                    <Phone size={13} /> {user.phone}
                  </span>
                ) : null}
                {user.website && user.website !== "-" ? (
                  <a href={toHref(user.website)} target="_blank" rel="noopener noreferrer">
                    <Globe size={13} /> Website
                  </a>
                ) : null}
                <span>ID #{user.id}</span>
              </div>
            </div>
          </div>

          <div className="ud-modal__badges">
            <span className={`ud-badge ${user.is_paid ? "ud-badge--paid" : "ud-badge--free"}`}>
              {user.is_paid ? "Paid" : "Free"}
            </span>
            <span className="ud-badge ud-badge--plan">{display(user.subscription_type)}</span>
            <span className="ud-badge ud-badge--soft">
              {display(user.subscription_status)}
            </span>
            <span className="ud-badge ud-badge--soft">
              {display(user.status)}
            </span>
            {user.user_account_status && user.user_account_status !== "-" ? (
              <span className="ud-badge ud-badge--soft">
                {display(user.user_account_status)}
              </span>
            ) : null}
          </div>

          {/* ── Impersonate action ── */}
          {canImpersonate && (
            <div className="ud-modal__impersonate-wrap">
              {!confirmPending ? (
                <button
                  type="button"
                  className="ud-modal__impersonate-btn"
                  onClick={() => setConfirmPending(true)}
                >
                  <UserCheck size={15} />
                  Impersonate
                </button>
              ) : (
                <div className="ud-modal__impersonate-confirm">
                  <AlertTriangle size={15} className="ud-modal__impersonate-confirm-icon" />
                  <p className="ud-modal__impersonate-confirm-text">
                    You are about to access the account of{" "}
                    <strong>{fullName || user.email}</strong>{" "}
                    ({user.email}).
                    <br />
                    This action is logged. Continue?
                  </p>
                  {impersonateError && (
                    <p className="ud-modal__impersonate-error">{impersonateError}</p>
                  )}
                  <div className="ud-modal__impersonate-confirm-actions">
                    <button
                      type="button"
                      className="ud-modal__impersonate-cancel"
                      onClick={() => {
                        setConfirmPending(false);
                        setImpersonateError(null);
                      }}
                      disabled={impersonating}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ud-modal__impersonate-yes"
                      onClick={handleImpersonateConfirm}
                      disabled={impersonating}
                    >
                      {impersonating ? "Starting…" : "Yes, impersonate"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ud-modal__content">
          <Section title="Profile">
            <InfoItem label="First name" value={user.first_name} />
            <InfoItem label="Last name" value={user.last_name} />
            <InfoItem label="Username" value={user.username} />
            <InfoItem label="Gender" value={user.gender} />
            <InfoItem label="Date of birth" value={user.date_of_birth} />
            <InfoItem label="Preferred language" value={user.language} />
            <InfoItem label="Account type (system)" value={user.user_type} />
            <InfoItem label="Default landing page" value={user.default_landing_page} />
          </Section>

          <Section title="Contact & location">
            <InfoItem label="Email" value={user.email} />
            <InfoItem label="Phone" value={user.phone} />
            <InfoItem label="Website" value={user.website} isUrl />
            <InfoItem label="Country" value={user.country} />
            <InfoItem label="Timezone" value={user.timezone} />
            <InfoItem label="Address line 1" value={user.address_line_1} />
            <InfoItem label="Address line 2" value={user.address_line_2} />
            <InfoItem label="City" value={user.city} />
            <InfoItem label="State" value={user.state} />
            <InfoItem label="ZIP" value={user.zip} />
            <InfoItem label="Businesses" value={user.businesses} />
            <InfoItem label="Communication preferences" value={user.communication_preferences} />
          </Section>

          <Section title="Subscription & billing">
            <InfoItem label="Plan" value={user.subscription_type} />
            <InfoItem label="Billing cycle" value={user.plan_type} />
            <InfoItem label="Paid / Free" value={user.is_paid ? "Paid" : "Free"} />
            <InfoItem label="Payment method" value={user.payment_method} />
            <InfoItem label="Subscription status" value={user.subscription_status} />
            <InfoItem label="Subscription date" value={user.subscription_date} />
            <InfoItem label="Trial ends" value={user.trial_ends_at} />
            <InfoItem label="Card on file" value={cardLabel} />
            <InfoItem label="Stripe customer ID" value={user.stripe_customer_id} />
          </Section>

          <Section title="Security & access">
            <InfoItem label="Account status" value={user.user_account_status} />
            <InfoItem label="User status" value={user.status} />
            <InfoItem label="Google 2FA" value={user.google_2fa_status} />
            <InfoItem label="Last login" value={user.last_login_date} />
            <InfoItem label="Last login IP" value={user.last_login_ip} />
            <InfoItem label="User agent" value={user.user_agent} />
          </Section>

          <Section title="Growth & referrals">
            <InfoItem label="Referral code" value={user.referral_code} />
            <InfoItem label="Referral link" value={user.referral_link} isUrl />
            <InfoItem label="Facebook" value={user.facebook_page_url} isUrl />
            <InfoItem label="Twitter / X" value={user.twitter_page_url} isUrl />
            <InfoItem label="Instagram" value={user.instagram_page_url} isUrl />
            <InfoItem label="GitHub" value={user.github_page_url} isUrl />
            <InfoItem label="Slack" value={user.slack_page_url} isUrl />
            <InfoItem label="CodePen" value={user.codepin_page_url} isUrl />
          </Section>

          <Section title="Activity">
            <InfoItem label="Created at" value={user.created_at} />
            <InfoItem label="Updated at" value={user.updated_at} />
            <InfoItem label="Account age" value={user.account_age} />
            <InfoItem label="Notes" value={user.notes} />
          </Section>
        </div>
      </ModalBody>
    </Modal>
  );
}

export default UserDetailsModal;
